import React, { useState, useEffect } from 'react';
import UnifiedCalendarSystem from './UnifiedCalendarSystem';
import CalendarManagement from './CalendarManagement';
import CalendarSettings from './CalendarSettings';
import './CalendarPage.css';

const CalendarPage = () => {
  const [activeViews, setActiveViews] = useState({
    blank: true,
    event: true, // Enable school events by default
    eca: false,
    slp: false
  });

  const [activeTab, setActiveTab] = useState('calendar'); // 'calendar', 'management', or 'settings'
  
  // Cycle day settings
  const [cycleDayMode, setCycleDayMode] = useState('normal'); // 'normal' or 'cycle'

  const handleViewChange = (newActiveViews) => {
    setActiveViews(newActiveViews);
  };

  // Initialize activeViews when component mounts
  useEffect(() => {
    // This ensures the initial state is properly set
    setActiveViews({
      blank: true,
      event: true, // Enable school events by default
      eca: false,
      slp: false
    });
    
    // Load cycle day setting from localStorage
    const savedCycleMode = localStorage.getItem('calendar_cycle_day_mode');
    if (savedCycleMode) {
      setCycleDayMode(savedCycleMode);
    }
  }, []);

  // Save cycle day setting to localStorage
  const handleCycleDayModeChange = (newMode) => {
    setCycleDayMode(newMode);
    localStorage.setItem('calendar_cycle_day_mode', newMode);
  };

  const getPageTitle = () => {
    const activeTypes = [];
    
    if (activeViews.blank) activeTypes.push('基本檢視');
    if (activeViews.event) activeTypes.push('學校活動');
    if (activeViews.eca) activeTypes.push('課外活動');
    if (activeViews.slp) activeTypes.push('學習歷程');
    
    if (activeTypes.length === 0) {
      return '校曆表 - 請選擇檢視類型';
    } else if (activeTypes.length === 1) {
      return `校曆表 - ${activeTypes[0]}`;
    } else if (activeTypes.length === 2) {
      return `校曆表 - ${activeTypes[0]} + ${activeTypes[1]}`;
    } else if (activeTypes.length === 3) {
      return `校曆表 - ${activeTypes[0]} + ${activeTypes[1]} + ${activeTypes[2]}`;
    } else {
      return '校曆表 - 綜合檢視';
    }
  };

  const getPageDescription = () => {
    const descriptions = [];
    
    if (activeViews.blank) descriptions.push('基本校曆 (公眾假期、週末、工作日)');
    if (activeViews.event) descriptions.push('學校活動和週期日制度');
    if (activeViews.eca) descriptions.push('課外活動安排');
    if (activeViews.slp) descriptions.push('學習歷程活動');
    
    if (descriptions.length === 0) {
      return '請選擇要顯示的日曆類型';
    } else if (descriptions.length === 1) {
      return descriptions[0];
    } else {
      return `顯示: ${descriptions.join('、')}`;
    }
  };

  return (
    <div className="calendar-page">
      {/* Tab Navigation */}
      <div className="calendar-tabs">
        <button
          className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          📅 日曆檢視 (Calendar View)
        </button>
        <button
          className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
          onClick={() => setActiveTab('management')}
        >
          ⚙️ 數據管理 (Data Management)
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          🔧 設定 (Settings)
        </button>
      </div>

      {activeTab === 'calendar' ? (
        <>
          <div className="page-header">
            <h1 className="page-title">{getPageTitle()}</h1>
            <p className="page-description">{getPageDescription()}</p>
          </div>

          <UnifiedCalendarSystem 
            viewMode="blank"
            onViewChange={handleViewChange}
            cycleDayMode={cycleDayMode}
          />
        </>
      ) : activeTab === 'management' ? (
        <CalendarManagement />
      ) : (
        <CalendarSettings 
          cycleDayMode={cycleDayMode}
          onCycleDayModeChange={handleCycleDayModeChange}
        />
      )}

      <div className="calendar-info">
        <div className="info-section">
          <h3>學年制度說明</h3>
          <ul>
            <li><strong>學年期間:</strong> 2024年9月至2025年8月</li>
            <li><strong>週期日制:</strong> 採用6日週期制度，循環計算上課日</li>
            <li><strong>考試安排:</strong> 每學年兩次考試，中六配合文憑試時程</li>
            <li><strong>假期安排:</strong> 依香港公眾假期及學校行事曆</li>
          </ul>
        </div>

        {activeViews.event && (
          <div className="info-section">
            <h3>週期日制度</h3>
            <div className="cycle-info">
              <div className="cycle-day-list">
                {[1, 2, 3, 4, 5, 6].map(day => (
                  <div key={day} className="cycle-day-item">
                    <span className="cycle-number">第{day}日</span>
                    <span className="cycle-description">
                      {day === 1 && '週期開始'}
                      {day === 6 && '週期結束'}
                    </span>
                  </div>
                ))}
              </div>
              <p className="cycle-note">
                * 週期日不包括週末、公眾假期和學校假期
              </p>
            </div>
          </div>
        )}

        {activeViews.eca && (
          <div className="info-section">
            <h3>課外活動類別</h3>
            <div className="eca-categories">
              <div className="category-item">
                <span className="category-color" style={{backgroundColor: '#e74c3c'}}></span>
                <span>學術類 Academic</span>
              </div>
              <div className="category-item">
                <span className="color-indicator" style={{backgroundColor: '#3498db'}}></span>
                <span>體育類 Sports</span>
              </div>
              <div className="category-item">
                <span className="color-indicator" style={{backgroundColor: '#2ecc71'}}></span>
                <span>藝術類 Arts</span>
              </div>
              <div className="category-item">
                <span className="color-indicator" style={{backgroundColor: '#f39c12'}}></span>
                <span>服務類 Service</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
